import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from '@mui/material'
import TermsOfUse from './TermsOfUse'
import PrivacyPolicy from './PrivacyPolicy'
import API from '../services/API'
import useIsMobile from '../helpers/useIsMobile.js'

const LegalAgreementModal = ({
  validTerms,
  validPrivacy,
  acceptedTermsVersion,
  acceptedPrivacyVersion,
  setUser,
}) => {
  const [tab, setTab] = useState('terms')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false)
  const [needsPrivacyAcceptance, setNeedsPrivacyAcceptance] = useState(false)

  // Small viewports (phones, or short windows such as a zoomed-in laptop
  // browser) can't afford the dialog margins or roomy chrome: the fixed
  // header/footer would otherwise squeeze the document down to a sliver.
  const isPhone = useIsMobile('sm')
  const isShort = useMediaQuery('(max-height: 600px)', { noSsr: true })
  const tight = isPhone || isShort
  // Stacking the footer costs a row of height, so only do it when there is
  // height to spare (portrait phones), never on short/landscape viewports.
  const stackFooter = isPhone && !isShort

  useEffect(() => {
    if (!validTerms || !validPrivacy) return

    setNeedsPrivacyAcceptance(validPrivacy !== acceptedPrivacyVersion)
    setNeedsTermsAcceptance(validTerms !== acceptedTermsVersion)
  }, [validTerms, acceptedTermsVersion, validPrivacy, acceptedPrivacyVersion])

  const needsAcceptance = needsTermsAcceptance || needsPrivacyAcceptance

  const availableTabs = useMemo(() => {
    const tabs = []
    if (needsTermsAcceptance) tabs.push('terms')
    if (needsPrivacyAcceptance) tabs.push('privacy')
    return tabs
  }, [needsTermsAcceptance, needsPrivacyAcceptance])

  useEffect(() => {
    if (availableTabs.length === 1) {
      setTab(availableTabs[0])
    } else if (!availableTabs.includes(tab) && availableTabs.length > 0) {
      setTab(availableTabs[0])
    }
  }, [availableTabs, tab])

  const canSubmit =
    (!needsTermsAcceptance || acceptedTerms) &&
    (!needsPrivacyAcceptance || acceptedPrivacy) &&
    !saving

  const handleAccept = async () => {
    try {
      setSaving(true)

      const res = await API.post('/account/accept-legal', {
        acceptedTermsVersion: validTerms,
        acceptedPrivacyVersion: validPrivacy,
      })

      if (res.status === 200) {
        setUser((prev) => ({
          ...prev,
          legal: {
            acceptedTermsVersion: validTerms,
            acceptedTermsAt: new Date().toISOString(),
            acceptedPrivacyVersion: validPrivacy,
            acceptedPrivacyAt: new Date().toISOString(),
          },
        }))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={needsAcceptance}
      fullWidth
      maxWidth="md"
      fullScreen={tight}
      disableEscapeKeyDown
      onClose={() => {}}
      PaperProps={{
        sx: {
          height: tight ? '100%' : '85vh',
          display: 'flex',
        },
      }}
    >
      <DialogTitle
        sx={{
          flexShrink: 0,
          ...(tight && { py: 1.25, px: 2, fontSize: '1.05rem' }),
        }}
      >
        Review and accept updated legal documents
      </DialogTitle>

      {/* The content scrolls as a last resort: the document keeps a usable
          minimum height and, if even that doesn't fit, the whole body scrolls
          rather than clipping. The acceptance controls live in the footer
          below so they are always on screen. */}
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {!isShort && (
          <Box sx={{ px: tight ? 2 : 3, pt: 2, pb: 1, flexShrink: 0 }}>
            <Typography variant="body2" color="text.secondary">
              To continue using AmbuNet, please review and accept the updated
              legal documents below.
            </Typography>
          </Box>
        )}

        {availableTabs.length > 1 && (
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant={tight ? 'fullWidth' : 'standard'}
            sx={{
              px: tight ? 0 : 2,
              flexShrink: 0,
              ...(isShort && {
                minHeight: 40,
                '& .MuiTab-root': { minHeight: 40, py: 0.5 },
              }),
            }}
          >
            {needsTermsAcceptance && (
              <Tab label={`Terms of Use (v${validTerms})`} value="terms" />
            )}
            {needsPrivacyAcceptance && (
              <Tab
                label={`Privacy Policy (v${validPrivacy})`}
                value="privacy"
              />
            )}
          </Tabs>
        )}

        <Box
          sx={{
            flex: '1 1 0',
            minHeight: 140,
            overflowY: 'auto',
            px: tight ? 2 : 3,
            py: 2,
          }}
        >
          {tab === 'terms' && needsTermsAcceptance && <TermsOfUse compact />}
          {tab === 'privacy' && needsPrivacyAcceptance && (
            <PrivacyPolicy compact />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: tight ? 2 : 3,
          py: tight ? 1 : 2,
          flexShrink: 0,
          // Portrait phones stack the checkboxes above a full-width button;
          // everything else keeps them side by side to save height.
          flexDirection: stackFooter ? 'column' : 'row',
          alignItems: stackFooter ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: stackFooter ? 1 : 2,
          '& > :not(style) ~ :not(style)': { ml: 0 },
        }}
      >
        <Stack spacing={tight ? 0 : 0.5} sx={{ minWidth: 0 }}>
          {needsTermsAcceptance && (
            <FormControlLabel
              control={
                <Checkbox
                  size={tight ? 'small' : 'medium'}
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
              }
              label={`I have read and agree to the Terms of Use (v${validTerms})`}
              slotProps={{
                typography: { variant: tight ? 'body2' : 'body1' },
              }}
              sx={{ mr: 0 }}
            />
          )}

          {needsPrivacyAcceptance && (
            <FormControlLabel
              control={
                <Checkbox
                  size={tight ? 'small' : 'medium'}
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                />
              }
              label={`I have read and acknowledge the Privacy Policy (v${validPrivacy})`}
              slotProps={{
                typography: { variant: tight ? 'body2' : 'body1' },
              }}
              sx={{ mr: 0 }}
            />
          )}
        </Stack>

        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleAccept}
          fullWidth={stackFooter}
          sx={{ flexShrink: 0 }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Accept and continue'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LegalAgreementModal
